import { useCallback, useState } from 'react';
import { bulkApolloMutationFactory } from 'utils/gql/bulkMutationFactory';
import { IntlModuleTypes } from '@enterprise/common';
import { getErrorMessage } from 'utils/gql/errors/errorsUtils';
import { useSnackbar } from 'utils/hooks/useSnackbar';
import { useApolloClient } from '@apollo/client';
import { TypedDocumentNode } from '@graphql-typed-document-node/core';
import { GraphQLError } from 'graphql/error/GraphQLError';
import { GqlResponseError } from 'utils/gql/errors/GqlResponseError';
import { GraphQLFormattedError } from 'graphql';

/**
  Handy tool when you need to deal with bulk actions.
  Use it together with "bulkMutationFactory"
  + with optional typed mutation (to get typed result out-of-the-box)

  Example:
  const { mutate } = useBulkMutation();
  const action = (uids: string[]) => {
    return mutate(bulkMutationFactory(typedQuery, uids), {
      errorMessage: 'My custom error. Skip it to get an error from the BE'
    })
  }

 */

export function useTypedBulkMutation<TData, TVariables extends { [key: string]: any }>(
  typedDocumentNode: TypedDocumentNode<TData, TVariables>
) {
  const { showSuccess, showError } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<GraphQLError[] | GraphQLFormattedError<Record<string, any>>[] | undefined>();
  const [data, setData] = useState<InferRecordValue<TData>[] | undefined>();
  const client = useApolloClient();

  const mutate = useCallback(
    async (
      variables: TVariables[],
      options?: {
        errorMessage?: IntlModuleTypes.FormatMessageFunction | string;
        successMessage?: IntlModuleTypes.FormatMessageFunction | string;
        context?: Record<string, any>;
      }
    ): Promise<InferRecordValue<TData>[] | null> => {
      setLoading(true);
      // if variable contains just one property, we can use it directly as variable otherwise use it as it is
      const bulkVariables = variables.map((v) => (Object.keys(v).length > 1 ? v : Object.values(v)[0]));
      const bulkPayload = bulkApolloMutationFactory(typedDocumentNode, bulkVariables);
      setErrors(undefined);
      try {
        const { data: responseData, errors: gqlErrors } = await client.mutate<Record<string, TData>>({
          mutation: bulkPayload.apolloQuery,
          variables: bulkPayload.payload,
          context: options?.context
        });
        const response = Object.values(responseData ?? {}) as unknown as InferRecordValue<TData>[];
        const totalCount = variables.length;
        if (response?.length) {
          if (options?.successMessage) {
            showSuccess(
              typeof options.successMessage === 'string'
                ? options.successMessage
                : options.successMessage({ totalCount })
            );
          }
          setData(response);
        }

        if (gqlErrors) {
          setErrors([...gqlErrors]);
          if (options?.errorMessage) {
            showError(
              typeof options.errorMessage === 'string' ? options.errorMessage : options.errorMessage?.({ totalCount })
            );
          } else {
            showError(new GqlResponseError(gqlErrors).message);
          }
        }
        return response ?? [];
      } catch (e: any) {
        const totalCount = Object.keys(bulkPayload.payload).length;
        if (options?.errorMessage) {
          showError(
            typeof options.errorMessage === 'string' ? options.errorMessage : options.errorMessage?.({ totalCount })
          );
        } else {
          showError(getErrorMessage(e).message);
        }

        return null;
      } finally {
        setLoading(false);
      }
    },
    [typedDocumentNode, client, showSuccess, showError]
  );

  return {
    set: mutate,
    data,
    errors,
    loading
  };
}

type InferRecordValue<T> = T extends Record<string, infer Z> ? Z : never;
